import { useMemo, useState } from "react";
import { persons, parties, edges, edgeTypes } from "../../../mocks/gossip";

const nameMap: Record<string, string> = Object.fromEntries(
  [...persons, ...parties].map((n) => [n.id, n.name])
);
const kindMap: Record<string, string> = Object.fromEntries(
  [...persons.map((p) => ({ id: p.id, kind: p.role })), ...parties.map((p) => ({ id: p.id, kind: p.kind }))].map(
    (n) => [n.id, n.kind]
  )
);

const dotCls: Record<string, string> = Object.fromEntries(edgeTypes.map((t) => [t.type, t.cls]));
const textCls: Record<string, string> = Object.fromEntries(edgeTypes.map((t) => [t.type, t.text]));

export default function GraphMap() {
  const [focusId, setFocusId] = useState<string>("all");
  const [hidden, setHidden] = useState<string[]>([]);

  const isPerson = (id: string) => persons.some((p) => p.id === id);

  const visibleEdges = useMemo(() => {
    let list = edges;
    if (focusId !== "all") {
      list = list.filter((e) => e.source === focusId || e.target === focusId);
    }
    if (hidden.length) list = list.filter((e) => !hidden.includes(e.type));
    return list;
  }, [focusId, hidden]);

  const toggleType = (type: string) =>
    setHidden((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const focusNode = focusId === "all" ? null : [...persons, ...parties].find((n) => n.id === focusId);
  const linkCount = (id: string) => edges.filter((e) => e.source === id || e.target === id).length;

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFocusId("all")}
          className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition ${
            focusId === "all"
              ? "bg-[#b4372a] text-white"
              : "border border-[#eadccb] bg-white text-[#5a5049] hover:border-[#d9a06b]"
          }`}
        >
          全部连线
        </button>
        {[...persons, ...parties].map((n) => (
          <button
            key={n.id}
            onClick={() => setFocusId(focusId === n.id ? "all" : n.id)}
            className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition ${
              focusId === n.id
                ? "bg-[#b4372a] text-white"
                : "border border-[#eadccb] bg-white text-[#5a5049] hover:border-[#d9a06b]"
            }`}
          >
            {n.name}
            <span className="ml-1 text-xs opacity-70">{linkCount(n.id)}</span>
          </button>
        ))}
      </div>

      {focusNode && (
        <div className="mt-5 rounded-lg border border-[#eadccb] bg-white p-5 md:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white ${
                  isPerson(focusNode.id) ? "bg-[#b4372a]" : "bg-[#b98a2f]"
                }`}
              >
                {isPerson(focusNode.id) ? "人" : "方"}
              </span>
              <div>
                <h3 className="font-serif-sc text-xl font-bold text-[#2a2320]">{focusNode.name}</h3>
                <p className="text-sm text-[#7a6f66]">{kindMap[focusNode.id]}</p>
              </div>
            </div>
            <span className="text-sm text-[#8a5a20]">与其直接相连 {linkCount(focusNode.id)} 条关系</span>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3">
        {visibleEdges.map((e, i) => {
          const other = e.source === focusId ? e.target : focusId === "all" ? null : e.source;
          return (
            <div
              key={i}
              className="flex flex-col gap-3 rounded-lg border border-[#eadccb] bg-white p-4 sm:flex-row sm:items-center"
            >
              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white ${dotCls[e.type]}`}>
                <i className="ri-link" aria-hidden="true"></i>
                {e.type}
              </span>
              <div className="flex flex-1 flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-[#2a2320]">{nameMap[e.source]}</span>
                <span className="flex h-5 items-center px-1 text-[#b98a2f]">
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </span>
                <span className="font-semibold text-[#2a2320]">{nameMap[e.target]}</span>
              </div>
              <p className="w-full text-sm leading-relaxed text-[#7a6f66] sm:max-w-md sm:shrink sm:text-right">
                {e.note}
                {other ? `（当前聚焦 ${nameMap[other]}）` : ""}
              </p>
            </div>
          );
        })}
        {visibleEdges.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#e2c9ab] p-8 text-center text-sm text-[#7a6f66]">
            当前筛选下暂无符合的连线，可在下方图例中重新勾选关系类型。
          </div>
        )}
      </div>

      <div className="mt-6">
        <p className="mb-2 text-sm font-semibold text-[#5a5049]">关系类型图例（点击筛选）</p>
        <div className="flex flex-wrap gap-2">
          {edgeTypes.map((t) => {
            const off = hidden.includes(t.type);
            return (
              <button
                key={t.type}
                onClick={() => toggleType(t.type)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs whitespace-nowrap transition ${
                  off ? "border-[#eadccb] bg-[#f3eadf] text-[#a99b8c] opacity-60" : "border-[#eadccb] bg-white text-[#5a5049]"
                }`}
              >
                <span className={`h-2.5 w-2.5 rounded-full ${off ? "bg-[#d6c7b4]" : dotCls[t.type]}`}></span>
                <span className={off ? "" : textCls[t.type]}>{t.type}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}